import { create } from 'zustand';

export type Theme = 'dark' | 'light';
export type Font =
  | 'palatino'
  | 'times'
  | 'georgia'
  | 'arial'
  | 'helvetica'
  | 'system';

const FONT_IDS: Font[] = [
  'palatino',
  'times',
  'georgia',
  'arial',
  'helvetica',
  'system',
];

// All current fonts are system fonts — no Google Fonts network request needed.
// If a web font is added in the future, add its family string here.
const GOOGLE_FONT_FAMILIES: Partial<Record<Font, string>> = {};

interface ThemeState {
  theme: Theme;
  font: Font;
  setTheme: (theme: Theme) => void;
  setFont: (font: Font) => void;
  toggleTheme: () => void;
}

const THEME_KEY = 'waw-theme';
const FONT_KEY = 'waw-font';

function readTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const stored = window.localStorage.getItem(THEME_KEY);
  return stored === 'light' ? 'light' : 'dark';
}

function readFont(): Font {
  if (typeof window === 'undefined') return 'georgia';
  const stored = window.localStorage.getItem(FONT_KEY);
  if (stored && (FONT_IDS as string[]).includes(stored)) return stored as Font;
  return 'georgia';
}

function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

function ensureGoogleFont(font: Font): void {
  if (typeof document === 'undefined') return;
  const family = GOOGLE_FONT_FAMILIES[font];
  if (!family) return;
  const linkId = `gf-${font}`;
  if (document.getElementById(linkId)) return;
  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${family}&display=swap`;
  document.head.appendChild(link);
}

function applyFont(font: Font): void {
  if (typeof document === 'undefined') return;
  ensureGoogleFont(font);
  document.documentElement.setAttribute('data-font', font);
}

const initialTheme = readTheme();
const initialFont = readFont();
applyTheme(initialTheme);
applyFont(initialFont);

export const useThemeStore = create<ThemeState>()((set, get) => ({
  theme: initialTheme,
  font: initialFont,
  setTheme: (theme) => {
    window.localStorage.setItem(THEME_KEY, theme);
    applyTheme(theme);
    set({ theme });
  },
  setFont: (font) => {
    window.localStorage.setItem(FONT_KEY, font);
    applyFont(font);
    set({ font });
  },
  toggleTheme: () => {
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
    window.localStorage.setItem(THEME_KEY, next);
    applyTheme(next);
    set({ theme: next });
  },
}));
